import React from 'react'
import { Button, Input, Text } from '../../components/ui'
import { usePermission } from '../../hooks/usePermissions'
import {
  calcularProjecaoNr1,
  COMISSAO_PADRAO,
  fmtMoeda,
} from '../../utils/calculadoraNr1'
import { buildCalculadoraNr1Document } from './buildCalculadoraNr1Document'
import { pdfExportService } from '../../services/pdfExportService'
import {
  comercialCalculadorasService,
  DEFAULT_CALCULADORA_PARAMETROS,
} from '../../services/comercialCalculadoras'

const primaryText = '!text-primary-500'

const MetricRow: React.FC<{
  label: string
  value: string
  detail?: string
  tone?: 'default' | 'sem' | 'com'
}> = ({ label, value, detail, tone = 'default' }) => {
  const borderClass =
    tone === 'sem'
      ? 'border-[#F0C07044] dark:border-amber-700/40'
      : tone === 'com'
        ? 'border-[#008b9544] dark:border-[#008b95]/40'
        : 'border-gray-200 dark:border-gray-700'

  const labelClass =
    tone === 'sem'
      ? '!text-[#A0660A] dark:!text-amber-400 font-semibold'
      : tone === 'com'
        ? `${primaryText} font-semibold`
        : undefined

  const valueClass =
    tone === 'sem'
      ? 'font-extrabold !text-[#7A4A00] dark:!text-amber-200'
      : tone === 'com'
        ? `font-extrabold ${primaryText}`
        : 'font-semibold text-gray-900 dark:text-white'

  return (
    <div className={`flex justify-between gap-4 py-2 border-b last:border-b-0 ${borderClass}`}>
      <Text size="sm" color={tone === 'default' ? 'muted' : undefined} className={labelClass}>
        {label}
      </Text>
      <div className="flex items-baseline gap-2 shrink-0 text-right flex-wrap justify-end">
        {detail && (
          <Text size="xs" color="muted" className="font-mono">
            {detail} =
          </Text>
        )}
        <Text size="sm" className={valueClass}>
          {value}
        </Text>
      </div>
    </div>
  )
}

const isCampoPreenchido = (value: string) => value.trim() !== ''

const CalculadoraNr1Tab: React.FC = () => {
  const { hasPermission: canDownloadInfo } = usePermission('comercial_calculadoras', 'download_info')
  const [vidas, setVidas] = React.useState('')
  const [valorIndividual, setValorIndividual] = React.useState('')
  const [comissaoPerc, setComissaoPerc] = React.useState('')
  const [parametros, setParametros] = React.useState(DEFAULT_CALCULADORA_PARAMETROS)
  const [comissaoError, setComissaoError] = React.useState<string | null>(null)
  const [exportingPdf, setExportingPdf] = React.useState(false)

  React.useEffect(() => {
    const loadParametros = async () => {
      try {
        const config = await comercialCalculadorasService.getParametrosConfig()
        setParametros(config)
      } catch (error) {
        console.error('Erro ao carregar parâmetros da calculadora NR-1:', error)
        setParametros(DEFAULT_CALCULADORA_PARAMETROS)
      }
    }
    void loadParametros()
  }, [])

  React.useEffect(() => {
    setComissaoPerc((prev) => {
      const num = parseFloat(prev)
      if (!Number.isFinite(num) || num <= parametros.comissaoPercentualMaximo) return prev
      return String(parametros.comissaoPercentualMaximo)
    })
  }, [parametros.comissaoPercentualMaximo])

  const handleComissaoChange = (raw: string) => {
    if (raw === '') {
      setComissaoPerc('')
      setComissaoError(null)
      return
    }

    const normalized = raw.replace(',', '.')
    if (!/^\d*\.?\d*$/.test(normalized)) return

    const num = parseFloat(normalized)
    if (Number.isNaN(num)) {
      setComissaoPerc(raw)
      setComissaoError(null)
      return
    }

    if (num < 0) {
      setComissaoPerc('0')
      setComissaoError(null)
      return
    }

    if (num > parametros.comissaoPercentualMaximo) {
      setComissaoPerc(String(parametros.comissaoPercentualMaximo))
      setComissaoError(
        `O percentual máximo permitido é ${parametros.comissaoPercentualMaximo}%.`
      )
      return
    }

    setComissaoPerc(raw)
    setComissaoError(null)
  }

  const todosCamposPreenchidos =
    isCampoPreenchido(vidas) &&
    isCampoPreenchido(valorIndividual) &&
    isCampoPreenchido(comissaoPerc)

  const projecao = React.useMemo(() => {
    if (!todosCamposPreenchidos) return null

    const vidasNum = parseFloat(vidas)
    const valorNum = parseFloat(valorIndividual.trim())
    const comissaoNum = parseFloat(comissaoPerc.replace(',', '.'))

    if (!Number.isFinite(vidasNum) || vidasNum < 0) return null
    if (!Number.isFinite(valorNum) || valorNum <= 0) return null
    if (!Number.isFinite(comissaoNum) || comissaoNum < 0) return null

    const comissaoLimitada = Math.min(comissaoNum, parametros.comissaoPercentualMaximo)

    return calcularProjecaoNr1(vidasNum, valorNum, comissaoLimitada, parametros.nr1ValorIndividual)
  }, [
    vidas,
    valorIndividual,
    comissaoPerc,
    todosCamposPreenchidos,
    parametros.nr1ValorIndividual,
    parametros.comissaoPercentualMaximo,
  ])

  const valorIndividualNum =
    valorIndividual.trim() !== '' ? parseFloat(valorIndividual) : null

  const canExportPdf =
    todosCamposPreenchidos &&
    projecao !== null &&
    valorIndividualNum !== null &&
    !Number.isNaN(valorIndividualNum)

  const handleExportPdf = async () => {
    if (!canDownloadInfo || !projecao || valorIndividualNum === null || Number.isNaN(valorIndividualNum)) return
    setExportingPdf(true)
    try {
      const config = buildCalculadoraNr1Document({
        vidas,
        valorIndividual,
        comissaoPerc,
        projecao,
        nr1ValorIndividual: parametros.nr1ValorIndividual,
        valorIndividualNum,
      })
      await pdfExportService.download(config)
    } catch (err: unknown) {
      console.error('Erro ao exportar PDF da calculadora NR-1:', err)
      const message = err instanceof Error ? err.message : 'Erro ao gerar PDF. Tente novamente.'
      alert(message)
    } finally {
      setExportingPdf(false)
    }
  }

  const limparDados = () => {
    setVidas('')
    setValorIndividual('')
    setComissaoPerc('')
    setComissaoError(null)
  }

  return (
    <div className="px-6 py-4 space-y-6">
      <div className="flex gap-3 items-start py-3 px-4 rounded-r-[10px] bg-[#008b95]/10 dark:bg-[#008b95]/15 border-l-[3px] border-l-[#008b95] dark:border-l-[#008b95]">
        <span className="text-[#008b95] dark:text-[#008b95] shrink-0 text-base leading-none mt-0.5" aria-hidden>
          ⓘ
        </span>
        <Text size="sm" className={`${primaryText} opacity-90 font-semibold leading-relaxed`}>
          Preencha os dados do subgrupo -{' '}
          <strong className={`font-extrabold ${primaryText}`}>número de vidas</strong>,{' '}
          <strong className={`font-extrabold ${primaryText}`}>valor individual</strong> e{' '}
          <strong className={`font-extrabold ${primaryText}`}>percentual de comissão</strong> - para
          visualizar a projeção de comissionamento com e sem o{' '}
          <strong className={`font-extrabold ${primaryText}`}>PASI NR-1</strong>. Ao final, use a
          simulação da carteira completa para uma visão consolidada de todos os seus contratos.
        </Text>
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <Text size="lg" className="text-gray-900 dark:text-white">
            Dados do subgrupo
          </Text>
          <Button variant="outline" size="sm" type="button" onClick={limparDados}>
            Limpar
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Text size="sm" className="mb-1 block text-gray-700 dark:text-gray-300">
              Número de vidas
            </Text>
            <Input
              type="number"
              min={0}
              value={vidas}
              onChange={(e) => setVidas(e.target.value)}
              placeholder="Ex: 100"
            />
          </div>
          <div>
            <Text size="sm" className="mb-1 block text-gray-700 dark:text-gray-300">
              Valor individual (R$)
            </Text>
            <Input
              type="number"
              min={0}
              step="0.01"
              value={valorIndividual}
              onChange={(e) => setValorIndividual(e.target.value)}
              placeholder="0,00"
            />
          </div>
          <div>
            <Text size="sm" className="mb-1 block text-gray-700 dark:text-gray-300">
              Comissão (%) - máx. {parametros.comissaoPercentualMaximo}%
            </Text>
            <Input
              type="number"
              min={0}
              max={parametros.comissaoPercentualMaximo}
              step="0.1"
              value={comissaoPerc}
              onChange={(e) => handleComissaoChange(e.target.value)}
              placeholder={String(COMISSAO_PADRAO)}
              aria-invalid={comissaoError ? true : undefined}
            />
            {comissaoError && (
              <Text size="xs" className="mt-1 text-red-600 dark:text-red-400">
                {comissaoError}
              </Text>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="relative mb-6 pb-4 border-b-2 border-[#008b95] dark:border-[#008b95]">
          <Text
            size="2xl"
            weight="bold"
            className={`text-center uppercase tracking-wide ${primaryText}`}
          >
            Projeção de comissionamento
          </Text>
          {canDownloadInfo && (
          <div className="hidden mt-3 flex justify-end sm:mt-0 sm:absolute sm:right-0 sm:top-1/2 sm:-translate-y-1/2">
            <Button
              variant="outline"
              size="sm"
              type="button"
              disabled={!canExportPdf || exportingPdf}
              onClick={() => void handleExportPdf()}
            >
              {exportingPdf ? 'Gerando PDF...' : 'Exportar PDF'}
            </Button>
          </div>
          )}
        </div>
        {todosCamposPreenchidos &&
        projecao &&
        valorIndividualNum !== null &&
        !Number.isNaN(valorIndividualNum) ? (
          <>
            <Text size="sm" color="muted" className="mb-4">
              {fmtMoeda(valorIndividualNum)} por vida · {projecao.vidas} vidas
            </Text>

            <div className="mb-6">
              <MetricRow label="Número de vidas" value={String(projecao.vidas)} />
              <MetricRow label="Prêmio base" value={fmtMoeda(projecao.base)} />
              <MetricRow
                label="PASI NR-1 adicional"
                detail={`${projecao.vidas} (Vidas) × ${fmtMoeda(parametros.nr1ValorIndividual)} (Valor individual NR-1)`}
                value={fmtMoeda(projecao.nr1)}
              />
              <MetricRow label="Prêmio total" value={fmtMoeda(projecao.total)} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="rounded-[10px] border-[1.5px] border-[#F0C070] bg-[#FEF3E2] dark:bg-amber-950/40 dark:border-amber-600 p-4">
                <Text
                  size="sm"
                  className="font-extrabold uppercase tracking-wide !text-[#A0660A] dark:!text-amber-400 mb-3"
                >
                  Sem PASI NR-1
                </Text>
                <MetricRow tone="sem" label="Comissão mensal" value={fmtMoeda(projecao.sem)} />
                <MetricRow tone="sem" label="Comissão anual" value={fmtMoeda(projecao.sem * 12)} />
              </div>
              <div className="rounded-[10px] border-[1.5px] border-[#008b95] bg-[#008b95]/10 dark:bg-[#008b95]/15 dark:border-[#008b95] p-4">
                <Text
                  size="sm"
                  className={`font-extrabold uppercase tracking-wide mb-3 ${primaryText}`}
                >
                  Com PASI NR-1
                </Text>
                <MetricRow tone="com" label="Comissão mensal" value={fmtMoeda(projecao.com)} />
                <MetricRow tone="com" label="Comissão anual" value={fmtMoeda(projecao.com * 12)} />
              </div>
            </div>

            <div className="rounded-xl bg-[#008b95] dark:bg-[#008b95] shadow-lg shadow-[#008b95]/25 dark:shadow-[#008b95]/40 px-5 py-5 md:px-6 md:py-6 mb-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Text size="xl" weight="bold" className="!text-white/90">
                  Ganho adicional com PASI NR-1
                </Text>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <Text size="2xl" className="font-black !text-white tracking-tight">
                    +{fmtMoeda(projecao.difMensal)}/mês
                  </Text>
                  <Text size="2xl" className="font-extrabold !text-white/90">
                    (+{projecao.aum.toFixed(1)}%)
                  </Text>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6 mt-2 border-t-2 border-[#008b95] dark:border-[#008b95]">
              <div className="rounded-xl border-2 border-[#008b95]/40 dark:border-[#008b95]/50 bg-[#008b95]/10 dark:bg-[#008b95]/10 p-5 md:p-6">
                <Text
                  size="xs"
                  weight="bold"
                  className={`uppercase tracking-wider mb-2 block ${primaryText}`}
                >
                  Comissão mensal com PASI NR-1
                </Text>
                <Text size="4xl" weight="bold" className={primaryText}>
                  {fmtMoeda(projecao.com)}
                </Text>
              </div>
              <div className="rounded-xl border-2 border-[#008b95]/40 dark:border-[#008b95]/50 bg-[#008b95]/10 dark:bg-[#008b95]/10 p-5 md:p-6">
                <Text
                  size="xs"
                  weight="bold"
                  className={`uppercase tracking-wider mb-2 block ${primaryText}`}
                >
                  Comissão anual com PASI NR-1
                </Text>
                <Text size="4xl" weight="bold" className={primaryText}>
                  {fmtMoeda(projecao.com * 12)}
                </Text>
              </div>
            </div>
          </>
        ) : (
          <Text size="sm" color="muted" className="italic mt-2">
            Preencha todos os campos do subgrupo para visualizar a projeção.
          </Text>
        )}
      </div>
    </div>
  )
}

export default CalculadoraNr1Tab
